import { Link, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  ArrowUpCircleIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  CpuChipIcon,
  LifebuoyIcon,
  MagnifyingGlassCircleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { ChartBarIcon } from '@heroicons/react/24/solid';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import { useAuth } from '@/store/hooks/useAuth';
import '@/components/Scrollytelling/scrollytelling.css';
import './activation.css';

type ActivationTool = {
  id: string;
  title: string;
  description: string;
  hint: string;
  to: string;
  icon: ReactNode;
};

const ICON_STYLE = { width: '1.5rem', height: '1.5rem', color: 'var(--activation-magenta)' } as const;

const AIDA_TOOL: ActivationTool = {
  id: 'ai',
  title: 'AIDA',
  description: 'DISGENET AI',
  hint: 'Ask anything',
  to: '/Assistant',
  icon: <img src="/static/images/eyesAida.svg" alt="" style={ICON_STYLE} />,
};

const MCP_TOOL: ActivationTool = {
  id: 'mcp',
  title: 'MCP Connector',
  description: 'AI agents',
  hint: 'Native to agents',
  to: '/Tools?section=mcp',
  icon: <CpuChipIcon style={ICON_STYLE} />,
};

const SHARED_ACTIVATION_TOOLS: ActivationTool[] = [
  {
    id: 'advanced-search',
    title: 'Advanced Search',
    description: 'Multi-entity query',
    hint: 'Complex queries',
    to: '/advancedSearch',
    icon: <MagnifyingGlassCircleIcon style={ICON_STYLE} />,
  },
  {
    id: 'enrichment',
    title: 'Enrichment',
    description: 'Functional analysis',
    hint: 'Gene set enrichment',
    to: '/enrichment',
    icon: (
      <ChartBarIcon
        style={{
          ...ICON_STYLE,
          transform: 'translateY(-1px) rotate(-90deg) rotateX(180deg)',
        }}
      />
    ),
  },
  {
    id: 'api',
    title: 'REST API',
    description: 'Programmatic access',
    hint: 'Query the database',
    to: '/interactive-console',
    icon: <img src="/static/images/icons/api-icon.svg" alt="" style={ICON_STYLE} />,
  },
];

const BUBBLE_SIZE = 128;

function ToolBubble({ tool }: { tool: ActivationTool }) {
  return (
    <Link to={tool.to} className="activation-page__bubble scrolly-s4-bubble" role="listitem">
      <div className="scrolly-s4-bubble__ring" style={{ width: BUBBLE_SIZE, height: BUBBLE_SIZE }}>
        <span className="scrolly-s4-bubble__icon">{tool.icon}</span>
        <span className="scrolly-s4-bubble__title">{tool.title}</span>
        <span className="scrolly-s4-bubble__desc">{tool.description}</span>
      </div>
      <span className="scrolly-s4-bubble__hint">{tool.hint}</span>
    </Link>
  );
}

const ActivationContent = () => {
  const navigate = useNavigate();
  const { isAcademic, isTrial } = useAuth();
  const activationTools = [isAcademic ? MCP_TOOL : AIDA_TOOL, ...SHARED_ACTIVATION_TOOLS];

  return (
    <section className="activation-page">
      <div className="activation-page__shell">
        <header className="activation-page__hero">
          <SharedBadge
            variant="primaryLight"
            size="xs"
            className="activation-page__badge mb-0"
            icon={<CheckCircleIcon className="w-3.5 h-3.5" aria-hidden />}
            text="Account activated"
          />
          <h1 className="activation-page__title mt-2">
            Welcome to <span className="activation-page__title-accent">DISGENET Advanced</span>
          </h1>
          <p className="activation-page__intro">
            Your free trial is live. Start searching for the most recent and relevant data on disease
            genomics.
          </p>
          <SharedBadge
            size="xs"
            className="activation-page__trial mt-2 mb-0"
            icon={<ClockIcon className="w-3.5 h-3.5" aria-hidden />}
            text="Your trial period ends in 7 days"
          />
          <div className="activation-page__ctas mt-2">
            <SharedButton
              type="button"
              variant="primary"
              size="sm"
              weight="xl"
              arrow="right"
              iconLeft={<TableCellsIcon className="w-4 h-4" aria-hidden />}
              onClick={() => navigate('/search')}
            >
              Browse data
            </SharedButton>
            <SharedButton
              type="button"
              variant="primaryOutline"
              size="sm"
              weight="xl"
              iconLeft={<MagnifyingGlassIcon className="w-4 h-4" aria-hidden />}
              onClick={() => navigate('/')}
            >
              Start searching
            </SharedButton>
          </div>
        </header>

        <section className="activation-page__tools" aria-labelledby="activation-tools-heading">
          <div className="activation-page__tools-head">
            <div>
              <p className="activation-page__eyebrow">Explore the platform</p>
              <h2 id="activation-tools-heading" className="activation-page__tools-title">
                Fit for <span className="text-pink">YOUR</span> needs
              </h2>
            </div>
            <button type="button" className="activation-page__tools-link" onClick={() => navigate('/Tools')}>
              View all tools →
            </button>
          </div>

          <div className="activation-page__bubbles" role="list">
            <div className="activation-page__bubbles-row">
              {activationTools.map(tool => (
                <ToolBubble key={tool.id} tool={tool} />
              ))}
            </div>
          </div>
        </section>

        <footer className="activation-page__support">
          <div className="activation-page__docs">
            <SharedBadge size="xs" className="activation-page__docs-badge mb-2" text="Documentation" />
            <h2 className="activation-page__docs-title">Start with the DISGENET Docs</h2>
            <p className="activation-page__docs-body">
              Learn how the knowledge base is built and how to query associations, scores and evidence
              effectively.
            </p>
            <div className="activation-page__docs-pills">
              <SharedButton
                type="button"
                size="xs"
                weight="lg"
                className="activation-page__docs-pill ml-0"
                iconLeft={<SparklesIcon className="w-3.5 h-3.5" aria-hidden />}
                onClick={() => navigate('/about')}
              >
                Getting started
              </SharedButton>
              <SharedButton
                type="button"
                size="xs"
                weight="lg"
                className="activation-page__docs-pill ml-0"
                iconLeft={<BookOpenIcon className="w-3.5 h-3.5" aria-hidden />}
                onClick={() => navigate('/docs')}
              >
                Browse documentation
              </SharedButton>
            </div>
          </div>

          <div className="activation-page__aside">
            <div className="activation-page__aside-card">
              <span className="activation-page__aside-icon activation-page__aside-icon--support" aria-hidden>
                <LifebuoyIcon />
              </span>
              <div className="activation-page__aside-copy">
                <h3 className="activation-page__aside-title">Need a hand?</h3>
                <p className="activation-page__aside-body">Our team can help you get unstuck fast.</p>
                <button
                  type="button"
                  className="activation-page__aside-link"
                  onClick={() => navigate('/Support')}
                >
                  Contact support →
                </button>
              </div>
            </div>
            {isTrial && !isAcademic ? (
              <div className="activation-page__aside-card">
                <span className="activation-page__aside-icon activation-page__aside-icon--demo" aria-hidden>
                  <CalendarDaysIcon />
                </span>
                <div className="activation-page__aside-copy">
                  <h3 className="activation-page__aside-title">Book a live demo</h3>
                  <p className="activation-page__aside-body">See the platform with your use case in mind.</p>
                  <button
                    type="button"
                    className="activation-page__aside-link"
                    onClick={() => navigate('/book-a-demo')}
                  >
                    Schedule a call →
                  </button>
                </div>
              </div>
            ) : null}
            {isAcademic ? (
              <div className="activation-page__aside-card">
                <span
                  className="activation-page__aside-icon activation-page__aside-icon--upgrade"
                  aria-hidden
                >
                  <ArrowUpCircleIcon />
                </span>
                <div className="activation-page__aside-copy">
                  <h3 className="activation-page__aside-title">Upgrade your plan</h3>
                  <p className="activation-page__aside-body">Unlock the full DISGENET Advanced experience.</p>
                  <button
                    type="button"
                    className="activation-page__aside-link"
                    onClick={() => navigate('/plans')}
                  >
                    View plans →
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </footer>
      </div>
    </section>
  );
};

export default ActivationContent;
